import {FC, useCallback, useEffect, useMemo, useState} from 'react'
import AsyncCreatableSelect from 'react-select/async-creatable'
import {v2_get_request_api} from '../api/get'
import {useAuth} from '../app/modules/auth'
import RSelect from 'react-select'
import AsyncSelect from 'react-select/async'
import {FieldProps} from './Form'
import {useQuery} from '@tanstack/react-query'
import {useTenantT} from '../i18n'

type SelectProps = FieldProps & {
  formik?: any
  idx?: number
  value?: any
  error?: boolean
  placeholder: string
  onSelect: (value: {value: any; label: string}) => void
  portal?: boolean
}

export const Select = (props: SelectProps) => {
  const t = useTenantT()
  const [dependencies, setDependencies] = useState<any[]>([])
  const [edit, setEdit] = useState<boolean>(false)
  const [complete, setComplete] = useState<boolean>(false)
  useEffect(
    () => {
      if (dependencies.length && edit) props.formik.setFieldValue(props.name, '')
      setComplete(true)
      setEdit(true)
    },
    // eslint-disable-next-line
    dependencies.map((dep) => props.formik.getFieldMeta(dep).value)
  )
  const queryKey = useMemo(() => {
    let queryParams: any = {key: []}
    if (props.optionsQuery?.queryKey) {
      queryParams.enabled = []
      queryParams.key = props.optionsQuery.queryKey.map((key) => {
        const dep: any = []
        const res = key.replace(/formik\.values\.([a-zA-Z0-9_[\].]+)/g, (_, fieldName) => {
          dep.push(fieldName)
          const value = props.formik.getFieldMeta(fieldName.replace('idx', `${props.idx}`))?.value
          if (!value) queryParams.enabled.push(false)
          return value
        })
        if (dep.length) setDependencies(dep)
        return res
      })
    }
    return queryParams
    // eslint-disable-next-line
  }, [props.optionsQuery?.queryKey, props.formik.values])
  const {data: result, isFetching} = useQuery({
    queryKey: queryKey.key,
    queryFn: v2_get_request_api,
    enabled: complete && (props.optionsQuery?.enabled ?? queryKey.enabled?.every(Boolean) ?? false),
    onSettled: () => setComplete(false),
  })
  const [ops, setOptions] = useState<{label: string; value: string}[]>(props.options || [])
  const options = useMemo(
    () =>
      ops?.map((op) => {
        return {...op, label: t(op.label)}
      }),
    // eslint-disable-next-line
    [ops]
  )
  const getOptions = useCallback(
    (item: any) => {
      let res: any = {
        label: item.name,
        value: item.id,
      }
      if (props.optionsQuery?.name) {
        const names = props.optionsQuery.name.split('.')
        let value = {...item}
        let id = item.id
        names.forEach((n: any) => {
          if (value) {
            value = value[n]
            id = value?.id || id
          }
        })
        if (props.optionsQuery?.id) {
          const ids = props.optionsQuery.id.split('.')
          let value = {...item}
          ids.forEach((idx: any) => {
            id = value?.[idx] || id
          })
        }
        res = {
          label: value,
          value: id,
        }
      }
      if (props.optionsQuery?.extras) {
        props.optionsQuery.extras.forEach((extra) => {
          res[extra] = item[extra]
        })
      }
      return res
    },
    [props.optionsQuery]
  )
  useEffect(() => {
    if (result && props.optionsQuery)
      setOptions(result.entities.map((item: any) => getOptions(item)))
    // eslint-disable-next-line
  }, [result])
  const selected = useMemo(() => {
    if (!['select', 'address'].includes(props.type!)) return
    let val
    if (options) val = options.find((o) => o.value === props.value)
    if (!val && props.value) {
      val = {value: props.value, label: props.value}
      if (props.viewName) val.label = props.formik.getFieldMeta(props.viewName).value || props.value
    }
    return val
    // eslint-disable-next-line
  }, [props.value, options, props.type, props.viewName])
  const multiSelected = useMemo(() => {
    if (props.type !== 'multi-select') return
    let val
    if (props.value) val = Array.isArray(props.value) ? props.value : [props.value]
    val = val?.map((v: any) => {
      if (!options) return {value: v, label: v}
      let found = options.find((o) => o.value === v)
      if (!found && v) {
        found = {value: v, label: v}
      }
      return found || {value: v, label: v}
    })
    return val
  }, [props.value, options, props.type])
  const fetchOptions = async (inputValue: string) => {
    if (!inputValue) return []

    try {
      const response = await v2_get_request_api({
        queryKey: [
          queryKey.key[0],
          (queryKey.key[1] ? queryKey.key[1] + '&' : '') + `search=${inputValue}`,
        ],
      })
      return response.entities.map((item: any) => getOptions(item))
    } catch (error) {
      console.error('Error fetching options:', error)
      return []
    }
  }
  // eslint-disable-next-line
  const loadOptions = useCallback((inputValue: string) => fetchOptions(inputValue), [])
  return (
    <div onClick={(e) => e.stopPropagation()}>
      {props.createFn ? (
        <AsyncCreatableSelect
          className='react-select-styled react-select-solid'
          classNamePrefix='react-select'
          cacheOptions
          defaultValue={props.type === 'multi-select' ? multiSelected : selected || null}
          defaultOptions={[{value: '', label: '-------'}, ...options]}
          isMulti={props.type === 'multi-select'}
          placeholder={props.placeholder}
          isDisabled={props.disabled}
          loadOptions={loadOptions}
          isLoading={isFetching}
          onChange={props.onSelect}
        />
      ) : props.searchFn ? (
        <AsyncSelect
          className='react-select-styled react-select-solid'
          classNamePrefix='react-select'
          menuPortalTarget={document.body}
          styles={{
            placeholder: (base: any) => ({
              ...base,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap', // truncate long placeholder
              maxWidth: 'calc(100% - 20px)',
            }),
            menuPortal: (base: any) => ({...base, zIndex: 9999}),
          }}
          cacheOptions
          defaultOptions={[{value: '', label: '-------'}, ...options]}
          placeholder={props.placeholder}
          value={props.type === 'multi-select' ? multiSelected : selected || null}
          isMulti={props.type === 'multi-select'}
          isDisabled={props.disabled}
          onChange={props.onSelect}
          isSearchable={true}
          loadOptions={props.searchFn}
          isLoading={isFetching}
        />
      ) : (
        <RSelect
          className='react-select-styled react-select-solid'
          classNamePrefix='react-select'
          menuPortalTarget={props.portal ? document.body : null}
          styles={{
            placeholder: (base: any) => ({
              ...base,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap', // truncate long placeholder
              maxWidth: 'calc(100% - 20px)',
            }),
            menuPortal: (base: any) => ({...base, zIndex: 9999}),
          }}
          options={[{value: '', label: '-------'}, ...options]}
          placeholder={props.placeholder}
          onChange={props.onSelect}
          value={props.type === 'multi-select' ? multiSelected : selected || null}
          isDisabled={props.disabled}
          isSearchable={true}
          isMulti={props.type === 'multi-select'}
        />
      )}
    </div>
  )
}

export const EmailCreatableSelect: FC<{
  name: string
  placeholder?: string
  formik: any
  selectFn?: (val: any) => void
  createField?: string
}> = ({name, placeholder, formik, selectFn, createField}) => {
  const {token} = useAuth()
  const validateEmail = (email: string) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return re.test(email)
  }
  const [isFetching, setIsFetching] = useState(false)
  const getUser = async (email: string) => {
    setIsFetching(true)
    const res = await v2_get_request_api({
      queryKey: ['user', `search=${email}`],
    })
    setIsFetching(false)
    return res.entities?.map((u: any) => ({
      value: u.id,
      label: `${u.first_name} ${u.last_name} (${u.email})`,
      ...u,
    }))
  }

  const loadOptions = (val: string, callback: (options: any[]) => void) => {
    if (!validateEmail(val)) {
      callback([])
      return
    }
    token ? getUser(val).then((options) => callback(options || [])) : callback([])
  }

  const value = formik.values[name] || formik.getFieldMeta(name).value
  const selected = useMemo(() => (value ? {label: value, value} : null), [value])
  let base = name.replace('.email', '').split('.')
  let subtext = base.pop()
  return (
    <>
      <AsyncCreatableSelect
        className='react-select-styled react-select-solid'
        classNamePrefix='react-select'
        cacheOptions
        loadOptions={loadOptions}
        defaultOptions
        placeholder={placeholder}
        isLoading={isFetching}
        value={selected}
        onChange={(e: any) => {
          if (e?.id) {
            formik.setFieldValue([...base, subtext].join('.'), e)
            formik.setFieldValue([...base, subtext + '_id'].join('.'), e.id)
          } else {
            formik.setFieldValue(name, e?.value || '')
            formik.setFieldValue([...base, subtext + '_id'].join('.'), undefined)
          }
          if (selectFn) selectFn(e)
        }}
        onCreateOption={(val: string) => {
          if (validateEmail(val)) {
            formik.setFieldValue([...base, 'id'].join('.'), undefined)
            formik.setFieldValue([...base, subtext + '_id'].join('.'), undefined)
            formik.setFieldValue(name, val)
            if (createField) formik.setFieldValue(createField, val)
          }
        }}
      />
    </>
  )
}
